import {useLDFlag} from 'launchdarkly-vue-client-sdk'
import {computed, type ComputedRef} from 'vue'

/**
 *   This will create a {@link FeatureEnabledService} for a given set of items and their associated LaunchDarkly flags.
 */

export function makeFeatureEnabledService<T>(
  ldKeyToItems: Record<string, T[]>,
  defaultValue: boolean
): FeatureEnabledService<T> {
  return new FeatureEnabledServiceFactory<T>().make(ldKeyToItems, defaultValue)
}

export class FeatureEnabledServiceFactory<T> {

  private createKeysToFlags(ldKeyToItems: Record<string, T[]>, defaultValue: boolean): Map<string, ComputedRef<boolean>> {
    return Object.keys(ldKeyToItems).reduce((acc: Map<string, any>, key: string) => {
      const flag = useLDFlag(key, defaultValue)
      acc.set(key, flag)
      return acc
    }, new Map<string, ComputedRef<boolean>>())
  }

  make(ldKeyToItems: Record<string, T[]>, defaultValue: boolean) {
    const keysToFlags = this.createKeysToFlags(ldKeyToItems, defaultValue)
    return new FeatureEnabledService<T>(ldKeyToItems, keysToFlags)
  }
}

/**
 *
 *   This is a generic way to group a collection of "items" under LaunchDarkly flags and see if that "item" is enabled
 *   for any feature flags.
 *
 *   Items can be a variety of things, usually some unique identifier, IE we may pair a shopping-cart feature with a
 *   collection of rule uuids and an advertising feature with some other set of rule uuids.
 *
 *   By asking this service is some rule is enabled RuleService.isAvailable('b94d9c45-b3e3-4f9e-817f-d8f45239a94d')
 *
 *   A: If the rule is not under any feature, it is not behind a feature flag and is available.
 *   B: The rule has any feature flags turned on, we know this rule is needed by an enabled feature and is available.
 *   C: The rule has feature flags, but they are all turned off, then the rule is not available.
 *
 */
export class FeatureEnabledService<T> {
  private keysToItems: Record<string, T[]>
  private keysToFlags: Map<string, ComputedRef<boolean>>

  constructor(keysToItems: Record<string, T[]>, keysToFlags: Map<string, ComputedRef<boolean>>) {
    this.keysToItems = keysToItems
    this.keysToFlags = keysToFlags
  }

  private getFlagsWithItem(item: T): ComputedRef<boolean>[] {
    const entriesWithFlag = Object.entries(this.keysToItems).filter(([_, value]) => {
      return value.some((keyItem) => keyItem === item)
    })

    return entriesWithFlag.map(([key]) => this.keysToFlags.get(key) as ComputedRef<boolean>)
  }

  /**
   * Returns a {@link ComputedRef} that indicates whether the item is enabled based on the associated feature flags.
   * this is reactive and will update when the flags change via websockets.
   * @param item
   */
  get(item: T): ComputedRef<boolean> {
    return computed(() => {

      const items = this.getFlagsWithItem(item);

      if (items.length === 0) {
        return true // No flags associated, item is enabled by default
      }

      // We NEED to evaluate all the computed refs, so they are tracked by Vue's reactivity system ( don't just use isEnabled )
      const values = items.map((it) => it.value);

      return values.some((value) => value === true);

    })
  }

  /**
   * Checks if the item is enabled based on the associated feature flags.
   *
   * - no associated flags = true
   * - at least one enabled associated flag = true
   * - all associated flags are disabled = false
   *
   * @param item
   */
  isEnabled(item: T): boolean {
    const featuresTrackingItem = this.getFlagsWithItem(item)

    if (featuresTrackingItem.length === 0) {
      return true
    }

    return featuresTrackingItem.some((feature) => feature.value === true)
  }

  /**
   * Checks if the item is disabled based on the associated feature flags.
   *
   * - no associated flags = false
   * - at least one enabled associated flag = false
   * - all associated flags are disabled = true
   *
   * @param item
   */
  isDisabled(item: T): boolean {
    return !this.isEnabled(item)
  }
}
